import Constants from "../constants";
import { adjustForInflation, getAgeAtRetirement, getYearsFromRetirementDate, round, roundUp } from "../utilities";
import { ChartView } from "./chartView";
import ViewMode from "./viewMode";

type ViewModeInfo = { isBaseMode: boolean, showInflationaryIncrease: boolean, showRealValue: boolean };

export const tickCount = 10;
export const tickUnits = [1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000];

export const createModernIncomeArray = function (baseIncome: number, extraIncome: number,
    extraIncomeYears: number, totalPlanYears: number, roundUpToNearest: number): Array<number> {

    const income = new Array<number>();

    for (let planYear = 0; planYear < Constants.MaxPlanYears; planYear++) {
        let totalIncome = 0;

        if (planYear < totalPlanYears) {
            totalIncome += baseIncome;

            if (planYear < extraIncomeYears) {
                totalIncome += extraIncome;
            }

            totalIncome = roundUp(totalIncome, roundUpToNearest);
        }

        income.push(totalIncome);
    }

    return income;
}

export const getDisabledClasses = function (isDisabled: boolean) {
    return {
        'disabled': isDisabled,
        'text-muted': isDisabled
    };
}

export const getCalendarYearText = function (calendarYear: number, startsOnJanFirst: boolean): string {
    if (startsOnJanFirst) {
        return `${calendarYear}`;
    }
    const abbrevNextCalendarYear = addOneAndGetLastTwoDigits(calendarYear);
    return `${calendarYear}/${abbrevNextCalendarYear}`;
};

function getViewModeInfo(viewMode: ViewMode): ViewModeInfo {
    const isBaseMode = viewMode == ViewMode.BeforeInflationNominal;
    const showInflationaryIncrease = viewMode == ViewMode.AfterInflationNominal || viewMode == ViewMode.AfterInflationReal;
    const showRealValue = viewMode == ViewMode.AfterInflationReal || viewMode == ViewMode.BeforeInflationReal;
    return { isBaseMode, showInflationaryIncrease, showRealValue };
}

export const calculateRequiredChartProjection = function(chartView: ChartView, nominalBeforeInflationIncome: Array<number>, step: number): Array<number> {
    const newData = Array<number>(nominalBeforeInflationIncome.length);

    const ageAtRetirement = getAgeAtRetirement() as number;
    let ageInPlanYear = ageAtRetirement;
    let nonInfAdjIncLastApplicableYear = 0;
    let infAdjIncInLastApplicableYear: number | null = null;

    const viewModeInfo = getViewModeInfo(chartView.viewMode);

    let yearsFromNowToRetirementDate = 0 as number;
    if (viewModeInfo.showRealValue) {
        yearsFromNowToRetirementDate = getYearsFromRetirementDate() as number;
    }

    let lastValueAfterInflationOnly = 0;

    nominalBeforeInflationIncome.forEach((value: number, index: number) => {
        let newAnnualIncome = value;

        logCalc(index, "newAnnualIncome", newAnnualIncome, index);

        if (viewModeInfo.showInflationaryIncrease) {
            logCalc(index, "applying inflation", ageInPlanYear, chartView.inflationDurationYears);
            if (ageInPlanYear > chartView.inflationDurationYears!) {
                logCalc(index, "doing inf calc: taking last adjusted figure as base")

                if (infAdjIncInLastApplicableYear == null) {
                    infAdjIncInLastApplicableYear = lastValueAfterInflationOnly;
                    nonInfAdjIncLastApplicableYear = nominalBeforeInflationIncome[index - 1];
                }

                const diff = newAnnualIncome - nonInfAdjIncLastApplicableYear;
                const currentYear = infAdjIncInLastApplicableYear + diff;

                logCalc(index, "inf detail", infAdjIncInLastApplicableYear, nonInfAdjIncLastApplicableYear, diff, currentYear);

                newAnnualIncome = currentYear;
            } else {
                logCalc(index, "doing inf calc: applying simple inf");
                const adjustedForInflation = adjustForInflation(newAnnualIncome, index);
                newAnnualIncome = round(adjustedForInflation, step);
            }

            lastValueAfterInflationOnly = newAnnualIncome;
            logCalc(index, "after inflation", newAnnualIncome);
        }

        if (viewModeInfo.showRealValue) {
            const yearsFactorForCurrentYear = yearsFromNowToRetirementDate - index;
            logCalc(index, "showRealValue", yearsFactorForCurrentYear, newAnnualIncome);
            const adjustedToRealValue = adjustForInflation(newAnnualIncome, yearsFactorForCurrentYear);
            newAnnualIncome = round(adjustedToRealValue, step);
            logCalc(index, "afterRealValue", newAnnualIncome);
        }

        logCalc(index, newAnnualIncome);
        logCalc(index, "");

        newData[index] = newAnnualIncome;
        ageInPlanYear += 1;
    });

    return newData;
}

export const calculateBaseValueFromProjectedValue = function(currentChartView: ChartView, amount: number, retirementYear: number, step: number): number {
    const viewModeInfo = getViewModeInfo(currentChartView.viewMode);

    if (viewModeInfo.isBaseMode) {
        return amount;
    }

    if (viewModeInfo.showInflationaryIncrease) {
        amount = adjustForInflation(amount, retirementYear * -1);
    }

    if (viewModeInfo.showRealValue) {
        const yearsFromNowToRetirementDate = getYearsFromRetirementDate() as number;
        const yearsFactorForCurrentYear = yearsFromNowToRetirementDate - retirementYear;
        amount = adjustForInflation(amount, yearsFactorForCurrentYear * -1);
    }

    return round(amount, step);
}

export const generateSteps = function(currentNumber: number, targetNumber: number, stepCount: number, postProcessor?: (value: number) => number): number[] {
    if (stepCount < 2) {
        throw new Error("Step count cannot be less than 2");
    }

    const steps = [currentNumber];
    const stepValue = (targetNumber - currentNumber) / (stepCount - 1);

    for (let i = 1; i < stepCount - 1; i++) {
        if (postProcessor != undefined) {
            steps.push(postProcessor(currentNumber + stepValue * i));
        } else {
            steps.push(currentNumber + stepValue * i);
        }
    }

    steps.push(targetNumber);

    return steps;
}

function logCalc(index: number, ...data: any[]) {
    const silent = true;

    if (silent) {
        return;
    }

    if (index != 11) {
        return;
    }

    const dataToLog = data.slice(1);

    console.log(data[0], ...dataToLog);
}

function addOneAndGetLastTwoDigits(num: number): string {
    const result = num + 1;
    const lastTwoDigits = String(result % 100).padStart(2, '0');
    return lastTwoDigits;
}
